import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Book, Gamepad2, Compass, Star, Palette, Trophy, Map, Store,
  Sparkles, Flame, ChevronRight, Zap, Clock, Users, Shield,
  BookOpen, Target, Award, Wand2, Heart, Pen, Quote, UserPlus, Rocket, GraduationCap,
  Brain, BarChart3, ArrowRight
} from "lucide-react";
import { useApp, API, IMG } from "../../context";
import {
  DayNightCycle, ParallaxStars, ForegroundSparkles,
  ShootingStars, PortalTransition
} from "../../components/shared";
import ThemeSpecialEffects from "../../components/ThemeSpecialEffects";
import InteractiveSkyGame from "../../components/InteractiveSkyGame";
import WeatherOverlay from "../../components/WeatherOverlay";
import NighttimeEnhancements from "../../components/NighttimeEnhancements";
import WelcomeBackSequence from "../../components/WelcomeBackSequence";
import { IsaiahMascotCore, useIsaiah, getWorldPose } from "../../components/isaiah";
import { useABTest } from "../../hooks/useABTest";

const TestimonialsCarousel = ({ navigate }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);
  // Lazy-load: only fetch testimonials when this section is about to enter
  // the viewport. Cuts a ~700ms blocking call off the home page's initial
  // network burst for everyone who never scrolls down.
  const sectionRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (!sectionRef.current || shouldLoad) return;
    const obs = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setShouldLoad(true);
          obs.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/site/testimonials`);
        setTestimonials(r.data.testimonials || []);
      } catch (e) { console.error(e); }
    })();
  }, [shouldLoad]);

  useEffect(() => {
    if (testimonials.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % Math.max(1, testimonials.length - 2));
    }, 7000);
    return () => clearInterval(intervalRef.current);
  }, [testimonials.length]);

  if (!testimonials.length) return <div ref={sectionRef} className="h-1" data-testid="testimonials-sentinel" />;

  const visible = testimonials.slice(currentIndex, currentIndex + 3);
  if (visible.length < 3 && testimonials.length >= 3) {
    visible.push(...testimonials.slice(0, 3 - visible.length));
  }

  return (
    <section className="relative z-10 px-4 py-8" data-testid="testimonials-carousel">
      <div className="max-w-3xl mx-auto">
        <div className="rounded-3xl p-6 sm:p-8 border-2 border-pink-400/25 bg-gradient-to-br from-pink-500/8 via-purple-500/5 to-rose-500/8" style={{ boxShadow: "0 0 40px rgba(236,72,153,0.08), inset 0 1px 0 rgba(236,72,153,0.1)" }}>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-white font-fredoka font-bold text-lg sm:text-xl flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-pink-500/20 border border-pink-400/30">
                <Heart size={16} className="text-pink-300" />
              </div>
              What Families Say
            </h2>
            <button onClick={() => navigate("/testimonials")} className="text-pink-400 text-xs font-fredoka font-bold hover:text-pink-300 transition-colors" data-testid="view-all-testimonials">
              View All <ChevronRight size={12} className="inline" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {visible.map((t, i) => (
              <div key={`${t.id}-${i}`} onClick={() => navigate("/testimonials")}
                className="cursor-pointer p-4 rounded-2xl border border-pink-400/15 bg-white/8 hover:bg-white/12 hover:border-pink-400/25 hover:scale-[1.02] transition-all duration-300"
                data-testid={`carousel-testimonial-${i}`}>
                <div className="flex items-center gap-3 mb-2">
                  {t.photo_url ? (
                    <img src={t.photo_url} alt={t.name} loading="lazy" className="w-10 h-10 rounded-full object-cover border-2 border-pink-300/25 shadow-md shadow-pink-500/10" />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-fredoka font-bold text-sm border-2 border-pink-300/25" style={{ background: "linear-gradient(135deg, rgba(236,72,153,0.35), rgba(139,92,246,0.35))" }}>{t.name?.charAt(0)}</div>
                  )}
                  <div>
                    <p className="text-white text-sm font-fredoka font-bold drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">{t.name}</p>
                    <p className="text-white/60 text-[10px] font-medium font-nunito">{t.role === "kid" ? `Explorer, age ${t.age}` : "Parent"}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-1.5">{[...Array(5)].map((_, j) => <Star key={j} size={9} className="text-amber-400 fill-amber-400" />)}</div>
                <p className="text-white/85 text-[13px] leading-relaxed line-clamp-3 font-medium font-nunito">{t.text}</p>
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-1.5 mt-4">
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => setCurrentIndex(i)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentIndex ? "bg-pink-400 w-4" : "bg-white/20"}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------- helper: time-based sky -------- */
export { TestimonialsCarousel };

function useSkyTheme() {
  const h = new Date().getHours();
  if (h >= 6 && h < 10) return { period: "dawn", sky: "from-indigo-950 via-rose-900 to-amber-800", starOpacity: 0.2 };
  if (h >= 10 && h < 17) return { period: "day", sky: "from-sky-400 via-blue-500 to-indigo-600", starOpacity: 0 };
  if (h >= 17 && h < 20) return { period: "dusk", sky: "from-indigo-900 via-purple-800 to-orange-700", starOpacity: 0.4 };
  return { period: "night", sky: "from-[#141B2D] via-indigo-950 to-[#141B2D]", starOpacity: 1 };
}

/* -------- WORLD GUIDE CHARACTERS -------- */
const WORLD_GUIDES = {
  learn: { name: "Professor Owl", emoji: "🦉", color: "#22C55E", lines: ["The forest whispers with knowledge today!", "Every question plants a seed of wisdom.", "Shall we explore a new clearing?"] },
  arcade: { name: "Byte", emoji: "🤖", color: "#8B5CF6", lines: ["Systems online! Games are powered up!", "High scores are waiting to be broken!", "Challenge mode activated!"] },
  adventure: { name: "Captain Nova", emoji: "🧭", color: "#F97316", lines: ["New horizons detected ahead!", "The map shows uncharted territory!", "Adventure awaits beyond the mist!"] },
  art: { name: "Pixel", emoji: "🦊", color: "#EC4899", lines: ["Creativity is the greatest magic!", "Your imagination paints the world!", "Every masterpiece starts with one stroke."] },
  marketplace: { name: "Star Trader", emoji: "⭐", color: "#F59E0B", lines: ["Rare finds in stock today!", "Your stars have great power here!", "New treasures just arrived!"] },
  trophies: { name: "The Archivist", emoji: "📜", color: "#EAB308", lines: ["Your legend grows with every badge!", "The hall remembers all achievements.", "New records are being inscribed."] },
};

/* -------- data: MAIN WORLDS (prominent, top of map) -------- */
const MAIN_WORLDS = [
  { id: "learn", name: "Learning Forest", icon: Book, to: "/learn", color: "#22C55E", glow: "rgba(34,197,94,0.5)", desc: "Enchanted treehouses of knowledge", guide: WORLD_GUIDES.learn, mapX: 20, mapY: 42 },
  { id: "adventure", name: "Adventure Island", icon: Compass, to: "/adventures", color: "#F97316", glow: "rgba(249,115,22,0.5)", desc: "Mysteries await", guide: WORLD_GUIDES.adventure, mapX: 50, mapY: 24, comingSoon: true },
  { id: "arcade", name: "Arcade City", icon: Gamepad2, to: "/arcade", color: "#8B5CF6", glow: "rgba(139,92,246,0.5)", desc: "Neon game district", guide: WORLD_GUIDES.arcade, mapX: 80, mapY: 42 },
  { id: "art", name: "Art Studio", icon: Palette, to: "/art-studio", color: "#EC4899", glow: "rgba(236,72,153,0.5)", desc: "Create anything", guide: WORLD_GUIDES.art, mapX: 50, mapY: 76 },
];

/* -------- data: UTILITY NODES (secondary, shown as dock below map) -------- */
const UTILITY_NODES = [
  { id: "dashboard", name: "Dashboard", icon: Compass, to: "/my-world", color: "#D946EF", desc: "Your home hub" },
  { id: "trophies", name: "Trophy Hall", icon: Trophy, to: "/trophies", color: "#EAB308", desc: "Badge collection" },
  { id: "marketplace", name: "Marketplace", icon: Store, to: "/marketplace", color: "#F59E0B", desc: "Trade & discover" },
];

const useNewArrivals = () => {
  const [count, setCount] = useState(0);
  const [ref, setRef] = useState(null);
  // Lazy-load: only fetch the "new arrivals" count when this component
  // (the marketplace world tile) is actually scrolled into view. Cuts a
  // ~1s blocking call out of the home page's initial load for every
  // visitor who never makes it to the world map.
  useEffect(() => {
    if (!ref) return;
    let triggered = false;
    const obs = new IntersectionObserver(
      entries => {
        if (triggered) return;
        if (entries.some(e => e.isIntersecting)) {
          triggered = true;
          obs.disconnect();
          const lastVisit = localStorage.getItem("marketplace_last_visit") || "";
          const params = lastVisit ? `?since=${lastVisit}` : "";
          axios.get(`${API}/marketplace/new-count${params}`)
            .then(r => setCount(r.data.count || 0))
            .catch(() => {});
        }
      },
      { rootMargin: "200px" } // pre-fetch when within 200px of viewport
    );
    obs.observe(ref);
    return () => obs.disconnect();
  }, [ref]);
  return [count, setRef];
};

const HORIZON_ISLANDS = [
  { id: "ocean-cove", name: "Ocean Cove", color: "#0EA5E9", desc: "Deep sea secrets", to: "/worlds/ocean-cove", icon: "🌊", label: "Hidden Region", mapX: 7, mapY: 30 },
  { id: "safari-trail", name: "Safari Trail", color: "#D97706", desc: "Wildlife wonders", to: "/worlds/safari-trail", icon: "🦁", label: "Secret World", mapX: 21, mapY: 30 },
  { id: "space-station", name: "Space Station", color: "#7C3AED", desc: "Cosmic secrets", to: "/worlds/space-station", icon: "🛸", label: "Unlock at Level 5", mapX: 36, mapY: 30 },
  { id: "crystal-cavern", name: "Crystal Cavern", color: "#14B8A6", desc: "Hidden treasures", to: "/worlds/crystal-cavern", icon: "💎", label: "Mystery Zone", mapX: 50, mapY: 30 },
  { id: "mystery-mountain", name: "Mystery Mountain", color: "#6366F1", desc: "???", to: "#", icon: "🏔️", label: "Coming Soon", mapX: 64, mapY: 30 },
  { id: "secret-island", name: "Secret Island", color: "#059669", desc: "???", to: "#", icon: "🏝️", label: "Coming Soon", mapX: 78, mapY: 30 },
  { id: "treasure-harbor", name: "Treasure Harbor", color: "#DC2626", desc: "???", to: "#", icon: "🏴‍☠️", label: "Future World", mapX: 93, mapY: 30 },
];

/* ======== MOUSE SPARKLE TRAIL ======== */

const WorldMapNode = ({ world, onClick, progress, index, onHover }) => {
  const Icon = world.icon;
  const [hovered, setHovered] = useState(false);
  const guide = world.guide;
  const guideLineIdx = useMemo(() => guide ? Math.floor(Math.random() * guide.lines.length) : 0, [guide]);
  const isComingSoon = world.comingSoon === true;

  const handleEnter = () => {
    setHovered(true);
    onHover?.(world.id);
  };
  const handleLeave = () => {
    setHovered(false);
    onHover?.(null);
  };
  const handleClick = () => {
    if (isComingSoon) return;
    onClick?.();
  };

  return (
    <div
      className="absolute group cursor-pointer"
      style={{ left: `${world.mapX}%`, top: `${world.mapY}%`, zIndex: hovered ? 30 : 12, animation: `world-float ${5 + index * 0.7}s ease-in-out ${index * -0.8}s infinite` }}
      onClick={handleClick}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-testid={`world-node-${world.id}`}
    >
      {/* Pulsing glow ring */}
      <div className="absolute inset-0 -m-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `radial-gradient(circle, ${world.glow}, transparent 70%)`, animation: "ring-pulse 3s ease-out infinite" }} />

      {/* Ambient glow */}
      <div className="absolute inset-0 -m-4 rounded-full" style={{ background: `radial-gradient(circle, ${world.color}15, transparent 70%)`, animation: `star-pulse 4s ease-in-out infinite` }} />

      {/* Main node */}
      <div className={`relative w-24 h-24 md:w-32 md:h-32 rounded-2xl flex flex-col items-center justify-center transition-all duration-300 group-hover:scale-110 border-2 overflow-hidden`} style={{ borderColor: hovered ? world.color : `${world.color}50`, background: `linear-gradient(145deg, ${world.color}20, rgba(11,17,32,0.85))`, boxShadow: hovered ? `0 0 40px ${world.glow}, 0 0 80px ${world.color}30, 0 8px 32px rgba(0,0,0,0.5)` : `0 0 15px ${world.color}20, 0 4px 16px rgba(0,0,0,0.3)`, transform: hovered ? "translateY(-4px)" : "none" }}>
        {isComingSoon && (
          <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden rounded-xl">
            <div className="absolute w-[200%] text-center py-0.5 font-fredoka font-bold text-[7px] md:text-[8px] tracking-[0.12em] uppercase"
              style={{ background: "linear-gradient(135deg, #F97316, #FB923C)", color: "#fff", transform: "rotate(-35deg)", top: "38%", left: "-50%", textShadow: "0 1px 3px rgba(0,0,0,0.3)", boxShadow: "0 2px 12px rgba(249,115,22,0.35)" }}>
              Coming Soon &nbsp;&middot;&nbsp; Coming Soon &nbsp;&middot;&nbsp; Coming Soon
            </div>
          </div>
        )}
        <div className={`mb-1 ${isComingSoon ? "opacity-40" : ""}`} style={{ filter: `drop-shadow(0 0 8px ${world.color})` }}>
          <Icon size={32} className="md:hidden" style={{ color: world.color }} />
          <Icon size={42} className="hidden md:block" style={{ color: world.color }} />
        </div>
        <span className={`text-xs md:text-sm font-fredoka font-bold text-center leading-tight px-1 ${isComingSoon ? "text-white/40" : "text-white"}`} style={{ textShadow: "0 2px 8px rgba(0,0,0,0.5)" }}>{world.name}</span>
      </div>

      {/* Guide character peek */}
      {guide && (
        <div className="absolute -top-2 -right-2 text-base opacity-70 group-hover:opacity-100 transition-opacity" style={{ animation: `island-float 3s ease-in-out ${-index}s infinite` }}>
          {guide.emoji}
        </div>
      )}

      {/* Hover tooltip */}
      {hovered && (
        <div className={`absolute z-40 animate-[section-rise_0.2s_ease-out] ${world.mapY > 60 ? "left-full ml-3 top-1/2 -translate-y-1/2" : "left-1/2 -translate-x-1/2 -bottom-16 md:-bottom-20"}`} style={{ width: "11rem" }}>
          <div className="rounded-xl bg-black/70 border-2 border-white/20 p-2.5 shadow-2xl">
            {isComingSoon ? (
              <>
                <p className="text-orange-300 text-[11px] font-fredoka font-bold mb-1">Coming Soon!</p>
                <p className="text-white/70 text-[10px] leading-snug">Can't wait for you to join us in our adventures!</p>
              </>
            ) : (
              <>
                <p className="text-white/70 text-[10px] font-semibold mb-0.5">{world.desc}</p>
                {guide && (
                  <p className="text-[9px] mt-1 italic" style={{ color: `${world.color}CC` }}>
                    {guide.emoji} "{guide.lines[guideLineIdx]}"
                  </p>
                )}
              </>
            )}
          </div>
          {world.mapY <= 60 && <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 bg-black/70 border-l border-t border-white/20" />}
        </div>
      )}
    </div>
  );
};

/* ===== HORIZON MYSTERY NODE ===== */
const HorizonMysteryNode = ({ island, onClick, index }) => {
  const isFuture = island.to === "#";
  return (
    <div
      className={`absolute group ${isFuture ? "cursor-default" : "cursor-pointer"}`}
      style={{ left: `${island.mapX}%`, top: `${island.mapY}%`, zIndex: 8, animation: `horizon-float ${7 + index}s ease-in-out ${index * -1.2}s infinite` }}
      onClick={() => !isFuture && onClick()}
      data-testid={`horizon-${island.id}`}
    >
      {/* Mist layer */}
      <div className="absolute inset-0 -m-5 rounded-full" style={{ background: `radial-gradient(circle, ${island.color}08, transparent 60%)`, filter: "blur(8px)" }} />

      {/* Node */}
      <div className={`relative w-18 h-18 md:w-20 md:h-20 rounded-2xl flex flex-col items-center justify-center transition-all duration-500 border-2 ${isFuture ? "opacity-40" : "opacity-70 group-hover:opacity-100 group-hover:scale-110"}`} style={{ width: "4.5rem", height: "4.5rem", borderColor: isFuture ? `${island.color}20` : `${island.color}60`, background: `linear-gradient(145deg, ${island.color}12, rgba(11,17,32,0.9))`, boxShadow: `0 0 20px ${island.color}10` }}>
        <span className="text-2xl md:text-3xl" style={{ filter: isFuture ? "grayscale(0.5)" : "none" }}>{island.icon}</span>
      </div>

      {/* Lock shimmer for future worlds */}
      {isFuture && (
        <div className="absolute inset-0 -m-1 rounded-2xl overflow-hidden">
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, transparent 40%, ${island.color}15 50%, transparent 60%)`, animation: "title-shimmer 4s linear infinite", backgroundSize: "200% 200%" }} />
        </div>
      )}

      {/* Label */}
      <div className="absolute left-1/2 -translate-x-1/2 -bottom-6 whitespace-nowrap">
        <span className="text-xs md:text-sm font-fredoka font-bold text-white/60 group-hover:text-white/90 transition-colors" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}>{island.name}</span>
      </div>
    </div>
  );
};

/* ===== ISAIAH HERO PRESENCE ===== */
const HOMEPAGE_MESSAGES = [
  "Welcome to Isaiah's World!",
  "Where should we go today?",
  "So many worlds to explore!",
  "Let's have an awesome day!",
  "Click on me!",
];

const IsaiahHeroPresence = ({ user, streak, progress, hoveredWorld }) => {
  // Mount immediately, the `isaiah-entrance-rise` CSS animation plays
  // a transform-only entrance (translateY + scale), so no layout shift.
  // We used to mount-after-600ms with `visible` state, which caused the
  // parent flex column to grow post-mount and pushed everything below
  // down, visible as Isaiah "popping in from behind the header" on
  // hard-refresh. CLS bug, fixed by always rendering.
  const [message, setMessage] = useState("");
  const [messageKey, setMessageKey] = useState(0);
  const initialSetRef = useRef(false);

  // React to hovered world portals
  useEffect(() => {
    if (hoveredWorld) {
      const ctx = getWorldPose(hoveredWorld);
      setMessage(ctx.message);
      setMessageKey(prev => prev + 1);
    }
  }, [hoveredWorld]);

  // Set the initial greeting ONCE after we have stable data.
  // Without this guard, each of user/streak/progress loading in separately
  // caused the greeting to re-compute on every async resolution, producing
  // a visible text flicker during the first ~600ms after mount.
  useEffect(() => {
    if (initialSetRef.current) return;
    // Wait until at least one data source is meaningful (or a small settle delay
    // has passed for anonymous visitors where all three stay null).
    const anyData = user || streak || progress;
    const t = setTimeout(() => {
      if (initialSetRef.current) return;
      initialSetRef.current = true;
      setMessage(getGreeting(user, streak, progress));
    }, anyData ? 0 : 350);
    return () => clearTimeout(t);
  }, [user, streak, progress]);

  // Cycle messages periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (!hoveredWorld) {
        const msg = HOMEPAGE_MESSAGES[Math.floor(Math.random() * HOMEPAGE_MESSAGES.length)];
        setMessage(msg);
        setMessageKey(prev => prev + 1);
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [hoveredWorld]);

  return (
    <div className="isaiah-entrance-rise" data-testid="isaiah-hero-presence">
      <div className="flex flex-col items-center gap-2">
        <IsaiahMascotCore
          pose="idle"
          size="full-body"
          glow
          breathing
          entrance="rise"
          danceable
          testId="isaiah-hero-character"
          className="isaiah-hero-alive"
        />
        {message && (
          <div key={messageKey} className="isaiah-speech-enter max-w-[220px]" data-testid="isaiah-hero-speech">
            <div className="relative rounded-xl bg-black/50 border border-white/15 px-3 py-2 shadow-xl">
              <p className="text-blue-300/80 text-[9px] font-bold mb-0.5 uppercase tracking-wider">Isaiah</p>
              <p className="text-white/80 text-[11px] leading-relaxed font-nunito">{message}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function getGreeting(user, streak, progress) {
  if (!user) return "Welcome to Isaiah's World! Ready to explore?";
  if (streak?.current_streak >= 7) return `${streak.current_streak}-day streak! You're unstoppable!`;
  if (streak?.current_streak >= 3) return `${streak.current_streak} days strong! Keep going!`;
  if (progress?.level >= 5) return "Look how far you've come! New worlds await!";
  if (progress?.worlds_explored >= 3) return "You've explored so many worlds!";
  return "Welcome back, explorer! Where to today?";
}

/* ===== ADVENTURE PORTAL CARD ===== */
const AdventurePortal = ({ path, onClick }) => (
  <div onClick={onClick} className="group cursor-pointer relative overflow-hidden rounded-2xl border border-white/10 bg-black/30 transition-all duration-300 hover:scale-[1.04] hover:border-white/25" data-testid={`portal-${path.id}`}>
    {/* Portal energy ring */}
    <div className="absolute inset-0 opacity-30 group-hover:opacity-70 transition-opacity duration-500" style={{ background: `radial-gradient(ellipse at center, ${path.color}25, transparent 70%)` }} />
    {/* Animated border glow */}
    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ boxShadow: `inset 0 0 20px ${path.color}30, 0 0 25px ${path.color}20` }} />
    {/* Portal swirl effect */}
    <div className="absolute inset-0 opacity-0 group-hover:opacity-40 transition-opacity" style={{ background: `conic-gradient(from 0deg at 50% 50%, transparent, ${path.color}15, transparent, ${path.color}10, transparent)`, animation: "portal-spin 8s linear infinite" }} />
    <div className="relative p-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2 transition-transform group-hover:scale-110" style={{ background: `${path.color}20`, boxShadow: `0 0 15px ${path.color}30` }}>
        <span className="text-xl">{path.icon}</span>
      </div>
      <h3 className="text-white text-xs font-fredoka font-bold">{path.name}</h3>
      <p className="text-white/40 text-[10px] mt-0.5">{path.chapters} Chapters</p>
      <div className="mt-2 flex items-center gap-1 text-[9px] font-bold transition-colors" style={{ color: `${path.color}80` }}>
        Enter Portal <ChevronRight size={10} />
      </div>
    </div>
  </div>
);

/* ===== WORLD EVOLUTION INDICATORS ===== */
const TIER_PALETTES = {
  basic: ["#C4B5FD", "#FDE68A", "#A5F3FC", "#FBCFE8", "#BBF7D0"],
  warm: ["#FBBF24", "#F97316", "#FB923C", "#FDE68A", "#FCA5A5"],
  vibrant: ["#A78BFA", "#F472B6", "#34D399", "#FBBF24", "#38BDF8"],
  golden: ["#FBBF24", "#FDE68A", "#FFD700", "#F59E0B", "#FBBF24"],
  legendary: ["#FFD700", "#FFF7ED", "#FBBF24", "#E879F9", "#67E8F9"],
};

const WorldEvolutionLayer = ({ evolution }) => {
  const tier = evolution?.tier || 1;
  const constellationCount = evolution?.constellation_count || 3;
  const glowIntensity = evolution?.glow_intensity || 0.3;
  const showAurora = evolution?.aurora || false;
  const showSpecial = evolution?.special_particles || false;
  const palette = TIER_PALETTES[evolution?.color_palette || "basic"];

  return (
    <div className="absolute inset-0 pointer-events-none z-[5]">
      {/* Evolution constellations that grow with progress */}
      {Array.from({ length: constellationCount }, (_, i) => {
        const angle = (i / constellationCount) * Math.PI * 2;
        const r = 20 + (i % 4) * 8;
        const cx = 50 + Math.cos(angle) * r;
        const cy = 45 + Math.sin(angle) * r * 0.5;
        const size = tier >= 4 ? 3 : tier >= 2 ? 2 : 1.5;
        return (
          <div key={`evo-${i}`} className="absolute rounded-full" style={{
            width: size, height: size,
            left: `${cx}%`, top: `${cy}%`,
            background: palette[i % palette.length],
            boxShadow: `0 0 ${size * 4}px ${palette[i % palette.length]}, 0 0 ${size * 8}px ${palette[i % palette.length]}40`,
            animation: `star-pulse ${2 + (i % 3)}s ease-in-out ${i * 0.4}s infinite`,
          }} />
        );
      })}

      {/* Streak energy glow */}
      {(evolution?.streak_days || 0) >= 2 && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{
          width: `${80 + (evolution?.streak_days || 0) * 12}px`,
          height: `${80 + (evolution?.streak_days || 0) * 12}px`,
          background: `radial-gradient(circle, rgba(251,191,36,${Math.min(0.12, glowIntensity * 0.15)}), transparent 70%)`,
          animation: "ring-pulse 6s ease-in-out infinite",
        }} />
      )}

      {/* Aurora effect for tier 3+ */}
      {showAurora && (
        <div className="absolute inset-x-0 top-0 h-1/3 overflow-hidden">
          <div className="absolute inset-0" style={{
            background: `linear-gradient(180deg, rgba(139,92,246,${glowIntensity * 0.08}) 0%, rgba(34,211,238,${glowIntensity * 0.05}) 50%, transparent 100%)`,
            animation: "aurora-shift 20s ease-in-out infinite",
          }} />
        </div>
      )}

      {/* Special golden particles for tier 4+ */}
      {showSpecial && Array.from({ length: 8 }, (_, i) => (
        <div key={`sp-${i}`} className="absolute rounded-full" style={{
          width: 4, height: 4,
          left: `${10 + Math.random() * 80}%`, top: `${10 + Math.random() * 80}%`,
          background: "#FFD700",
          boxShadow: "0 0 12px #FFD700, 0 0 24px #FFD70060",
          animation: `star-pulse ${3 + Math.random() * 2}s ease-in-out ${i * 0.7}s infinite`,
        }} />
      ))}

      {/* Evolution tier badge */}
      {tier >= 2 && (
        <div className="absolute top-3 right-3 z-20">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border" style={{
            background: `rgba(0,0,0,0.3)`,
            borderColor: tier >= 4 ? "rgba(251,191,36,0.3)" : "rgba(255,255,255,0.1)",
          }}>
            <span className="text-xs">{tier >= 5 ? "👑" : tier >= 4 ? "⚡" : tier >= 3 ? "🌟" : "🔮"}</span>
            <span className="text-[9px] font-fredoka font-bold" style={{
              color: tier >= 4 ? "#FBBF24" : "#C4B5FD",
            }}>{evolution?.tier_name}</span>
          </div>
        </div>
      )}
    </div>
  );
};

/* ======== MAIN HOMEPAGE ======== */
export { useSkyTheme, WORLD_GUIDES, MAIN_WORLDS, UTILITY_NODES, useNewArrivals, HORIZON_ISLANDS, WorldMapNode, HorizonMysteryNode, HOMEPAGE_MESSAGES, IsaiahHeroPresence, getGreeting, AdventurePortal, TIER_PALETTES, WorldEvolutionLayer };
